import { useState } from 'react';
import { useElementSize } from '@shared/utils/hooks/useElementSize';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';

export const SEARCH_BOX_MAX_WIDTH = 288;
export const SEARCH_BOX_MIN_WIDTH = 160;

const sumWidths = (elements: ReadonlyArray<Element>) => elements.reduce((total, element) => total + element.getBoundingClientRect().width, 0);

/**
 * Desktop width of the filter bar's search box: as wide as {@link SEARCH_BOX_MAX_WIDTH}, giving
 * up width (down to {@link SEARCH_BOX_MIN_WIDTH}) only when that lets the filter pills fit on one
 * line. If they would wrap even at the minimum, shrinking buys nothing, so the box stays full width.
 *
 * Measured rather than left to flexbox: `max-width: max-content` on the wrapping pill row was
 * meant to give the pills priority, but the browser under-reported the row's one-line width
 * (630px for pills that needed more), so the pills wrapped while the search box stayed wide.
 *
 * - `rowRef`: the filter bar row; its content box is the space to share.
 * - `pillsRef`: wraps the pill row; the row's children are summed as if on one line.
 * - `searchGroupRef`: the search box (first child) plus whatever sits beside it.
 *
 * - `pillsKey`: changes whenever the pills may have changed width (the active filters).
 *
 * Returns the max until it can measure (server, jsdom).
 */
export const useSearchBoxWidth = (
  rowRef: React.RefObject<HTMLElement>,
  pillsRef: React.RefObject<HTMLElement>,
  searchGroupRef: React.RefObject<HTMLElement>,
  pillsKey: unknown,
): number => {
  const [width, setWidth] = useState(SEARCH_BOX_MAX_WIDTH);
  const { width: rowWidth } = useElementSize(rowRef);

  useIsomorphicLayoutEffect(() => {
    const row = rowRef.current;
    const pillRow = pillsRef.current?.firstElementChild;
    const searchGroup = searchGroupRef.current;
    const searchBox = searchGroup?.firstElementChild;
    if (!row || !pillRow || !searchGroup || !searchBox) return;

    const rowStyle = window.getComputedStyle(row);
    const rowContentWidth = row.clientWidth - parseFloat(rowStyle.paddingLeft) - parseFloat(rowStyle.paddingRight);
    const pills = Array.from(pillRow.children).filter(child => child.getBoundingClientRect().width > 0);
    const pillGap = parseFloat(window.getComputedStyle(pillRow).columnGap) || 0;
    const pillsOneLineWidth = sumWidths(pills) + pillGap * Math.max(pills.length - 1, 0);
    // Padding, gap and the trailing action: everything in the group that is not the box itself.
    const searchGroupExtras = searchGroup.getBoundingClientRect().width - searchBox.getBoundingClientRect().width;

    const room = Math.floor(rowContentWidth - pillsOneLineWidth - searchGroupExtras);
    const next = room >= SEARCH_BOX_MIN_WIDTH ? Math.min(room, SEARCH_BOX_MAX_WIDTH) : SEARCH_BOX_MAX_WIDTH;
    setWidth(prev => (prev === next ? prev : next));
  }, [rowRef, pillsRef, searchGroupRef, rowWidth, pillsKey]);

  return width;
};
